
import { Component, Vue, Prop } from 'vue-property-decorator'
import { actionTypesModule, commonModule, itemsModule, offersModule } from '@/store/modules/store-accessor'
import { formatAmount, getOptimalTextColor, installationPhotoCompressionSettings } from '@/helpers/data'
import dayjs from 'dayjs'
import { greekSearchFilter } from "@/helpers/data";
import imageCompression from 'browser-image-compression'
import PhotoCarouselViewer from '@/components/UI/PhotoCarouselViewer.vue'
import { TimeUtil } from '@/helpers/TimeUtil'

type OfferLineType = 'ACTION' | 'MATERIAL' | 'CUSTOM';

type OfferLineModel = {
  local_id: number;
  action_type?: any;
  time_unit?: any;
  material?: any;
  inventory_item_id?: string | null;
  inventory_unit?: any;
  is_unit_based?: boolean;
  description: string;
  quantity: number;
  unit_cost: number;
  discount_percentage: number;
  notes: string;
};

@Component({
  components: { PhotoCarouselViewer }
})
export default class AddOfferModal extends Vue {
  @Prop() payload: any
  @Prop({ default: false }) quickadd!: boolean
  @Prop() readonly installation: any

  greekSearchFilter = greekSearchFilter;

  isAddingOffer = false;
  validUntilMenu = false;
  lineItemsTouched = false;
  formRenderKey = 0;
  isDuplicating = false;
  pendingPhotoCompression = 0;
  photosToClone: OfferPhotoDto[] = [];

  offerModel = {
    title: '',
    notes: '',
    internal_notes: '',
    assignee_id: '',
    valid_until: '',
    installation_id: '',
    line_items: [] as Record<string, any>[],
  }

  selectedAssignee = null as any;

  photoModel = {
    add_photo: false,
    photos: [] as File[],
    notes: '',
    previewUrls: [] as string[],
  };

  lineTypes = {
    ACTION: false,
    MATERIAL: false,
    CUSTOM: false,
  }

  actionLines: OfferLineModel[] = [this.newLine('ACTION')];

  materialLines: OfferLineModel[] = [this.newLine('MATERIAL')];

  customLines: OfferLineModel[] = [this.newLine('CUSTOM')];

  titleRules = [
    (val: any) => !!val || this.$i18n.t("title_required"),
    (val: any) => val.length <= 200 || this.$i18n.t("offerTitleLengthRule"),
  ]

  notesRule = [
    (val: any) => !val || val.length <= 1000 || this.$i18n.t("form_notes_length_rule"),
  ]

  internalNotesRules = [
    (val: any) => val.length <= 1000 || this.$i18n.t("internalNotesLengthRule"),
  ]

  actionRules = [
    (val: any) => (!!val && val.text != "") || this.$i18n.t("action_required"),
  ]

  unitOptionRules = [
    (val: any) => ((!!val || val == '') && val.text.length > 0) || this.$i18n.t("unitOptionRule"),
  ]

  quantityRules = [
    (val: any) => Number(val) >= 1 || this.$i18n.t("requiredNonZeroQuantityRule"),
  ]

  unitCostRules = [
    (val: any) => val !== '' && val !== null || this.$i18n.t("priceOfferRequiredRule"),
    (val: any) => Number(val) >= 0 || this.$i18n.t("priceOfferNonNegativedRule"),
  ]

  materialRules = [
    (val: any) => {
      if (!val && val !== 0) return this.$i18n.t("materialFieldRequiredRule");
      if (typeof val === 'string') return val.trim().length > 0 || this.$i18n.t("materialFieldRequiredRule");
      return val.text?.trim().length > 0 || this.$i18n.t("materialFieldRequiredRule");
    }
  ]

  descriptionRules = [
    (val: any) => (!!val && val.text != "") || this.$i18n.t("descriptionRequiredRule"),
    (val: any) => val.length <= 200 || this.$i18n.t("descriptionLengthRule"),
  ]

  discountRule = [
    (val: any) => val !== '' && val !== null && Number(val) >= 0 && Number(val) <= 100 || this.$i18n.t("discountPercentageRule"),
  ]

  lineItemNotesRules = [
    (val: any) => {
      if (val != null)
        return val.length <= 500 || this.$i18n.t("offerLineItemNotesLengthRule");
      else return true;
    },
  ]

  photoRules = [
    () => this.photoModel.photos.length >= 1 || this.$t('photosRequired'),
    () => this.photoModel.photos.length <= 30 || this.$t('photoLengthRule'),
  ]

  photoNotesRules = [
    (val: string) => !val || val.length <= 50 || this.$t('notesForPhotoRule'),
  ]

  get assigneeIdCombo() {
    return commonModule.personnelCombo;
  }

  get actionsCombo() {
    return actionTypesModule.actionTypesCombo;
  }

  get hasSelectedLineType() {
    return this.lineTypes.ACTION || this.lineTypes.MATERIAL || this.lineTypes.CUSTOM;
  }

  get inventoryItemsCombo() {
    return commonModule.inventoryItemsCombo;
  }

  get itemsUnitsTypes() {
    return itemsModule.itemsUnitsCombo;
  }

  get timeChargedUnitCombo() {
    return commonModule.timeChargedUnitCombo;
  }

  get isDuplicateOffer() {
    return !!this.payload?.duplicateOffer;
  }

  get clonedOfferPhotos(): OfferPhotoDto[] {
    return this.photosToClone;
  }

  removeClonedOfferPhoto(photo: OfferPhotoDto) {
    this.photosToClone = this.photosToClone.filter((item) => item.id !== photo.id);
  }

  offerPhotoPath(photo: OfferPhotoDto): string {
    return `${photo.base_url.replace(/\/$/, '')}/${photo.src.replace(/^\//, '')}`;
  }

  formatOfferPhotoDate(date: string): string {
    return TimeUtil.formatDateTime(date as any);
  }

  get selectedLines() {
    const lines: OfferLineModel[] = [];

    if (this.lineTypes.ACTION) {
      lines.push(...this.actionLines);
    }

    if (this.lineTypes.MATERIAL) {
      lines.push(...this.materialLines);
    }

    if (this.lineTypes.CUSTOM) {
      lines.push(...this.customLines);
    }

    return lines;
  }

  get offerTotalCost() {
    return this.selectedLines.reduce((total, line) => total + this.lineTotal(line), 0);
  }

  async hideModal() {
    if (this.quickadd) {
      this.$emit('close-modal');
      return;
    }

    commonModule.hideModal();
  }

  get installationId(): string {
    return String(this.installation?.id || this.installation?.info?.id || this.payload?.installation?.info?.id || '');
  }

  async previewPhotos(files: File[] | null) {
    if (!files?.length) return;

    const newFiles = Array.from(files);
    const newPreviewUrls = newFiles.map((file) => URL.createObjectURL(file));
    this.photoModel.photos = [...this.photoModel.photos, ...newFiles];
    this.photoModel.previewUrls = [...this.photoModel.previewUrls, ...newPreviewUrls];
    this.pendingPhotoCompression += newFiles.length;
    const options = installationPhotoCompressionSettings();

    await Promise.all(newFiles.map(async (file) => {
      try {
        const compressedFile = await imageCompression(file, options);
        const index = this.photoModel.photos.indexOf(file);
        if (index !== -1) {
          this.$set(this.photoModel.photos, index, compressedFile);
        }
      } catch {
        return;
      } finally {
        this.pendingPhotoCompression = Math.max(0, this.pendingPhotoCompression - 1);
      }
    }));

    this.$nextTick(() => {
      const input = this.$refs.photosInput as Vue & { validate?: () => boolean };
      input?.validate?.();
    });
  }

  removePhoto(index: number) {
    const previewUrl = this.photoModel.previewUrls[index];
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    this.photoModel.photos.splice(index, 1);
    this.photoModel.previewUrls.splice(index, 1);

    if (this.photoModel.photos.length === 0) {
      this.clearPhotos();
    } else {
      this.$nextTick(() => {
        const input = this.$refs.photosInput as Vue & { validate?: () => boolean };
        input?.validate?.();
      });
    }
  }

  clearPhotos() {
    this.revokePhotoPreviewUrls();
    this.photoModel.photos = [];
    this.photoModel.previewUrls = [];
    this.pendingPhotoCompression = 0;

    const input = this.$refs.photosInput as Vue & {
      reset?: () => void;
      $refs?: { input?: HTMLInputElement };
    };
    input?.reset?.();
    if (input?.$refs?.input) {
      input.$refs.input.value = '';
    }
  }

  revokePhotoPreviewUrls() {
    this.photoModel.previewUrls.forEach((url) => URL.revokeObjectURL(url));
  }

  beforeDestroy() {
    this.revokePhotoPreviewUrls();
  }

  async mounted() {
    commonModule.initSnackbar({});
    this.isDuplicating = this.isDuplicateOffer;

    await Promise.all([
      commonModule.getPersonnelCombo(),
      actionTypesModule.getActionTypesCombo(),
      commonModule.getItemsCombo(),
      commonModule.getTimeChargedUnitCombo(),
      itemsModule.getItemsUnitsCombo(),
    ]);

    this.doDuplicateOffer();
  }

  newLine(lineType: OfferLineType): OfferLineModel {
    return {
      local_id: Math.random() * -1,
      action_type: lineType === 'ACTION' ? null : undefined,
      time_unit: null,
      material: lineType === 'MATERIAL' ? null : undefined,
      inventory_item_id: null,
      inventory_unit: null,
      is_unit_based: false,
      description: '',
      quantity: 1,
      unit_cost: 0,
      discount_percentage: 0,
      notes: '',
    };
  }

  addLine(lineType: OfferLineType) {
    this.linesByType(lineType).push(this.newLine(lineType));
  }

  deleteLine(lineType: OfferLineType, index: number) {
    const lines = this.linesByType(lineType);
    if (lines.length > 1) {
      lines.splice(index, 1);
    }
  }

  linesByType(lineType: OfferLineType): OfferLineModel[] {
    switch (lineType) {
      case 'ACTION':
        return this.actionLines;
      case 'MATERIAL':
        return this.materialLines;
      case 'CUSTOM':
        return this.customLines;
      default:
        return [];
    }
  }

  onActionTypeChange(actionLine: OfferLineModel) {
    if (!actionLine.action_type) return;

    actionLine.description = actionLine.action_type.text || '';
    actionLine.unit_cost = Number(actionLine.action_type.cost || 0);
    actionLine.time_unit = this.actionTimeUnitOption(actionLine);

    actionLine.quantity = 1; // Change it back to the default value as when the action does not have time_unit the user can not change the quantity
  }

  actionDefaultTimeUnit(actionLine: OfferLineModel) {
    const actionType = actionLine.action_type;
    const timeUnit = actionType?.time_unit || actionType?.unit?.unit || actionType?.unit;

    if (!actionType?.is_time_charged && !actionType?.time_unit) {
      return null;
    }

    return ['HOUR', 'DAY'].includes(String(timeUnit).toUpperCase()) ? String(timeUnit).toUpperCase() : null;
  }

  actionTimeUnitOption(actionLine: OfferLineModel) {
    const timeUnit = this.actionDefaultTimeUnit(actionLine);
    return this.timeUnitOption(timeUnit);
  }

  actionTimeUnit(actionLine: OfferLineModel) {
    const selectedUnit = actionLine.time_unit?.unit || actionLine.time_unit;
    const timeUnit = selectedUnit || this.actionDefaultTimeUnit(actionLine);
    return ['HOUR', 'DAY'].includes(String(timeUnit).toUpperCase()) ? String(timeUnit).toUpperCase() : null;
  }

  actionHasTimeUnit(actionLine: OfferLineModel) {
    return !!this.actionDefaultTimeUnit(actionLine);
  }

  lineTotal(line: OfferLineModel) {
    const quantity = Number(line.quantity || 0);
    const unitCost = Number(line.unit_cost || 0);
    const discountPercentage = Math.min(Math.max(Number(line.discount_percentage || 0), 0), 100);

    return quantity * unitCost * (1 - discountPercentage / 100);
  }

  formatBalance(balance: number) {
    return formatAmount(balance);
  }

  clearDate() {
    this.offerModel.valid_until = "";
  }

  timeUnitOption(timeUnit: string | null) {
    if (!timeUnit) return null;
    return this.timeChargedUnitCombo.find((item: any) => item.unit === timeUnit) || null;
  }

  inventoryUnitOption(inventoryUnit: string | null) {
    if (!inventoryUnit) return null;
    return (this.itemsUnitsTypes as any[]).find((item: any) => item.enum_text === inventoryUnit) || null;
  }

  doDuplicateOffer() {
    const offer = this.payload?.duplicateOffer as OfferDetailsDto | undefined;
    if (!offer) {
      this.isDuplicating = false;
      return;
    }

    this.offerModel = {
      ...this.offerModel,
      title: offer.title || '',
      notes: offer.notes || '',
      internal_notes: offer.internal_notes || '',
      valid_until: offer.valid_until ? dayjs(offer.valid_until).format('YYYY-MM-DD') : '',
    };
    this.selectedAssignee = this.assigneeIdCombo.find((assignee: any) => assignee.id === offer.assignee_id) || null;
    this.photosToClone = [...(offer.photos || [])];

    const lineItems = [...(offer.line_items || [])].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    const actionLines = lineItems.filter((lineItem) => lineItem.line_type === 'ACTION').map((lineItem) => this.duplicateActionLine(lineItem));
    const materialLines = lineItems.filter((lineItem) => lineItem.line_type === 'MATERIAL').map((lineItem) => this.duplicateMaterialLine(lineItem));
    const customLines = lineItems.filter((lineItem) => lineItem.line_type === 'CUSTOM').map((lineItem) => this.duplicateCustomLine(lineItem));

    this.lineTypes = {
      ACTION: actionLines.length > 0,
      MATERIAL: materialLines.length > 0,
      CUSTOM: customLines.length > 0,
    };
    this.actionLines = actionLines.length > 0 ? actionLines : [this.newLine('ACTION')];
    this.materialLines = materialLines.length > 0 ? materialLines : [this.newLine('MATERIAL')];
    this.customLines = customLines.length > 0 ? customLines : [this.newLine('CUSTOM')];

    this.$nextTick(() => {
      this.formRenderKey += 1;
      this.isDuplicating = false;
    });
  }

  duplicateBaseLine(lineType: OfferLineType, lineItem: OfferLineItemDto): OfferLineModel {
    const line = this.newLine(lineType);
    line.description = lineItem.description || '';
    line.quantity = Number(lineItem.quantity || 1);
    line.unit_cost = Number(lineItem.unit_cost || 0);
    line.discount_percentage = Number(lineItem.discount_percentage || 0);
    line.notes = lineItem.notes || '';
    return line;
  }

  duplicateActionLine(lineItem: OfferLineItemDto): OfferLineModel {
    const line = this.duplicateBaseLine('ACTION', lineItem);
    line.action_type = this.actionsCombo.find((action: any) => action.id === lineItem.action_type_id) || {
      id: lineItem.action_type_id,
      text: lineItem.action_type_text || lineItem.description,
      cost: lineItem.unit_cost,
      is_time_charged: !!lineItem.time_unit,
      time_unit: lineItem.time_unit,
    };
    line.description = lineItem.description || line.action_type?.text || '';
    line.time_unit = this.timeUnitOption(lineItem.time_unit);
    return line;
  }

  duplicateMaterialLine(lineItem: OfferLineItemDto): OfferLineModel {
    const line = this.duplicateBaseLine('MATERIAL', lineItem);
    line.material = this.inventoryItemsCombo.find((item: any) => item.inventory_item_id === lineItem.inventory_item_id) || {
      inventory_item_id: lineItem.inventory_item_id,
      text: lineItem.description,
      cost: lineItem.unit_cost,
      unit: lineItem.inventory_unit,
      is_unit_based: !!lineItem.inventory_unit,
    };
    line.inventory_item_id = lineItem.inventory_item_id;
    line.inventory_unit = this.inventoryUnitOption(lineItem.inventory_unit);
    line.is_unit_based = !!lineItem.inventory_unit || !!line.material?.is_unit_based;
    return line;
  }

  duplicateCustomLine(lineItem: OfferLineItemDto): OfferLineModel {
    return this.duplicateBaseLine('CUSTOM', lineItem);
  }

  buildLineItem(lineType: OfferLineType, line: OfferLineModel, sortOrder: number) {
    const lineItem: Record<string, any> = {
      line_type: lineType,
      description: line.description,
      quantity: Number(line.quantity),
      unit_cost: Number(line.unit_cost),
      discount_percentage: Number(Number(line.discount_percentage || 0).toFixed(2)),
      sort_order: sortOrder,
      notes: line.notes,
    };

    if (lineType === 'ACTION') {
      lineItem.action_type_id = line.action_type.id;

      const timeUnit = this.actionTimeUnit(line);
      if (timeUnit) {
        lineItem.time_unit = timeUnit;
      }
    }

    if (lineType === 'MATERIAL') {
      if (line.inventory_item_id) {
        lineItem.inventory_item_id = line.inventory_item_id;
      }

      const inventoryUnit = line.inventory_unit?.enum_text || line.inventory_unit;
      if (inventoryUnit) {
        lineItem.inventory_unit = inventoryUnit;
      }
    }

    return lineItem;
  }

  buildLineItems() {
    const lineItems: Record<string, any>[] = [];
    let sortOrder = 0;

    if (this.lineTypes.ACTION) {
      this.actionLines.forEach((line) => {
        lineItems.push(this.buildLineItem('ACTION', line, sortOrder));
        sortOrder += 1;
      });
    }

    if (this.lineTypes.MATERIAL) {
      this.materialLines.forEach((line) => {
        lineItems.push(this.buildLineItem('MATERIAL', line, sortOrder));
        sortOrder += 1;
      });
    }

    if (this.lineTypes.CUSTOM) {
      this.customLines.forEach((line) => {
        lineItems.push(this.buildLineItem('CUSTOM', line, sortOrder));
        sortOrder += 1;
      });
    }

    return lineItems;
  }

  optimalTextColor(color: string) {
    return getOptimalTextColor(color);
  }

  findItemUnit(unitType: Record<string, any>, material: OfferLineModel) {
    if (!unitType) return;

    let unitCombo: any = itemsModule.itemsUnitsCombo;
    const foundUnitType = (unitCombo).find((item: any) => item.enum_text == unitType.unit);
    material.description = unitType.text || unitType || '';
    material.inventory_unit = foundUnitType ? { text: foundUnitType.text, enum_text: foundUnitType.enum_text, decimals: foundUnitType.decimals } : null;
    material.is_unit_based = unitType.is_unit_based ?? false;
    material.unit_cost = unitType.cost ?? null;
    material.quantity = unitType.is_unit_based ? 1 : material.quantity;
    material.inventory_item_id = unitType.inventory_item_id ?? null;

    material.quantity = 1; // Change it back to the default value as when the action does not have time_unit the user can not change the quantity
  }

  async cloneOfferPhotos(sourceOffer: OfferDetailsDto, targetOffer: OfferDto): Promise<boolean> {
    const photoIds = this.clonedOfferPhotos.map((photo) => String(photo.id));
    if (photoIds.length === 0) return true;

    try {
      await offersModule.cloneOfferPhotos({
        installationId: String(targetOffer.installation_id || this.offerModel.installation_id),
        offerId: String(targetOffer.id),
        sourceOfferId: String(sourceOffer.id),
        photoIds,
        refresh: false,
      });
      return true;
    } catch {
      return false;
    }
  }

  async uploadSelectedOfferPhotos(targetOffer: OfferDto): Promise<boolean> {
    if (!this.photoModel.add_photo || this.photoModel.photos.length === 0) return true;

    try {
      const formData = new FormData();
      this.photoModel.photos.forEach((file) => formData.append('image', file));
      formData.append('notes', this.photoModel.notes.trim());

      await offersModule.createOfferPhoto({
        installationId: String(targetOffer.installation_id || this.offerModel.installation_id),
        offerId: String(targetOffer.id),
        photo: formData,
        refresh: false,
      });
      return true;
    } catch {
      return false;
    }
  }

  async doAddOffer() {
    this.lineItemsTouched = true;

    if (this.pendingPhotoCompression > 0 || !(this.$refs.form as Vue & { validate: () => boolean }).validate() || !this.hasSelectedLineType) {
      return;
    }

    this.isAddingOffer = true;

    try {
      this.offerModel.assignee_id = this.selectedAssignee?.id;
      this.offerModel.installation_id = this.installationId;
      this.offerModel.line_items = this.buildLineItems();

      const offer = await offersModule.createOffer(this.offerModel);

      const sourceOffer = this.payload?.duplicateOffer as OfferDetailsDto | undefined;
      const sourcePhotosExist = this.clonedOfferPhotos.length > 0;
      const selectedPhotosExist = this.photoModel.add_photo && this.photoModel.photos.length > 0;
      let photosUploaded = true;

      if (sourceOffer && sourcePhotosExist) {
        photosUploaded = await this.cloneOfferPhotos(sourceOffer, offer);
      }

      if (selectedPhotosExist) {
        photosUploaded = await this.uploadSelectedOfferPhotos(offer) && photosUploaded;
      }

      if (sourcePhotosExist || selectedPhotosExist) {
        try {
          await offersModule.refreshOfferPhotos({
            installationId: String(offer.installation_id || this.offerModel.installation_id),
            offerId: String(offer.id),
          });
        } catch {
          photosUploaded = false;
        }
      }

      if (!photosUploaded) {
        commonModule.initSnackbar({
          visible: true,
          color: 'error',
          timeout: 5000,
          text: this.$t('offerPhotoUploadFailed'),
        });
      }

      switch (this.$route.name) {
        case "offers":
          await offersModule.getAllOffers(this.offersQuery());
          break;
      }

      if (this.quickadd) {
        this.$emit('complete');
      } else {
        this.hideModal();
      }
    } finally {
      this.isAddingOffer = false;
    }
  }

  offersQuery() {
    const params = new URLSearchParams();

    Object.keys(this.$route.query).forEach((key) => {
      const value = this.$route.query[key];

      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null && item !== "") {
            params.append(key, item);
          }
        });
      } else if (value !== undefined && value !== null && value !== "") {
        params.set(key, value as string);
      }
    });

    params.set("page", (offersModule.offersPagination.page || 1).toString());
    params.set("limit", (offersModule.offersPagination.limit || 20).toString());

    const query = params.toString();
    return query ? `?${query}` : "";
  }
};
